import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../../library/editors/elements/d-checkbox.js';
import '../../library/editors/elements/d-edit-textarea.js';
import '../../library/elements/d-section.js';
import '../../library/lists/d-list-section';
import '../../library/elements/d-smooth-resize.js';
import '../../library/editors/elements/d-select-checkbox.js';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import { t } from 'src/i18n';
import type { MessageRecipient } from '../event-occurrences/message-recipient';
import type { MeetingMessageDialogInput } from './meeting-message-dialog-input';
import type { MeetingMessageDialogResult } from './meeting-message-dialog-result';
import type { DialogActionInput } from 'src/library/components/DialogActionInput';
import type { SelectCheckboxOption } from 'src/library/editors/elements/select-checkbox-option';
import type { PersonAsMessageRecipient } from 'src/content/event-occurrences/person-as-message-recipient';

/**
 *
 * USAGE:
 *    d-event-view
 *
 */
@customElement('meeting-message-dialog')
export class MeetingMessageDialog extends BaseDialog<MeetingMessageDialogInput, MeetingMessageDialogResult> {
  @property({ type: String })
  type: 'notice' | 'report' = 'notice';
  @property({ type: String })
  name = '';
  @property({ type: Boolean })
  isEmployeeInterview = false;
  @property({ type: Boolean })
  nccEnabled = false;
  @property({ type: Array })
  recipientEmployees: string[] = [];
  @property({ type: Array })
  recipientContacts: string[] = [];
  @property({ type: String })
  meetingMessageComment = '';
  @property({ type: Boolean })
  messageIncludeContent = false;
  @property({ type: Boolean })
  preventIncludeContent = false;
  @property({ type: Array })
  participants: string[] = [];
  @property({ type: Array })
  people: PersonAsMessageRecipient[] = [];
  @property({ type: String })
  classification: 'NONE' | 'CONFIDENTIAL' | 'PRIVATE' = 'NONE';
  @property({ type: Array })
  accessControl: string[] = [];
  @state()
  showAllEmployees = false;
  @state()
  showAllContacts = false;
  width = 600;

  protected get calculatedTitle(): string {
    return this.type === 'notice'
      ? t('meetings.messageDialogTitleNotice', { name: this.name })
      : t('meetings.messageDialogTitleReport', { name: this.name });
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('meetings.messageDialogCancel'), action: 'cancel' },
      { name: t('meetings.messageDialogSend'), action: 'send', main: true, disabled: this.sendDisabled },
    ];
  }

  /**
   * All employees are listed even though not all are selectable.
   * The UI explains the status of each one, so that the user know how to make them selectable if needed (e.g. add email)
   */
  private get employees(): PersonAsMessageRecipient[] {
    return this.people.filter((p) => {
      return p.type === 'employee';
    });
  }

  /**
   * All contacts are listed even though not all are selectable.
   * The UI explains the status of each one, so that the user know how to make them selectable if needed (e.g. add email)
   */
  private get contacts(): PersonAsMessageRecipient[] {
    return this.people.filter((p) => {
      return p.type === 'contact';
    });
  }

  private get sendDisabled(): boolean {
    return this.recipientEmployees.concat(this.recipientContacts).length === 0;
  }

  private get includeContentText() {
    return this.type === 'notice' ? t('meetings.messageIncludeAgenda') : t('meetings.messageIncludeReport');
  }

  private get prompt() {
    return this.type === 'notice' ? this.promptForNotice : this.promptForReport;
  }

  private get includeReportContentPromptPart() {
    if (this.preventIncludeContent) {
      return t('meetings.promptReportContentPrevented');
    }
    return t('meetings.promptReportContentAllowed');
  }

  private get promptForNotice() {
    return this.isEmployeeInterview ? t('meetings.promptNoticeEmployeeInterview') : t('meetings.promptNotice');
  }

  private get promptForReport() {
    return this.isEmployeeInterview
      ? t('meetings.promptReportEmployeeInterview')
      : t('meetings.promptReport') + this.includeReportContentPromptPart + '</p>';
  }

  private userAccessData(person: MessageRecipient) {
    const typeTerm = this.type === 'report' ? t('meetings.typeTermReport') : t('meetings.typeTermMeeting');
    if (!person.email) {
      return {
        disabled: true,
        text: t('meetings.accessNoEmail'),
      };
    }
    if (person.accessLevel === 'NONE') {
      if (this.type === 'notice') {
        return {
          disabled: false,
          text: t('meetings.accessNoTrinnvisNoLink', { typeTerm }),
        };
      }
      if (this.type === 'report') {
        return {
          disabled: !this.messageIncludeContent,
          text: this.messageIncludeContent
            ? t('meetings.accessNoTrinnvisNoLink', { typeTerm })
            : t('meetings.accessNoTrinnvis'),
        };
      }
    }
    if (this.classification !== 'NONE' && !this.accessControl.includes(person.uuid)) {
      if (this.type === 'report') {
        return {
          disabled: !this.messageIncludeContent || this.preventIncludeContent,
          text: this.messageIncludeContent
            ? t('meetings.accessNoAccessNoLinkTo', { typeTerm })
            : t('meetings.accessNoAccessTo', { typeTerm }),
        };
      }
      return {
        disabled: false,
        text: t('meetings.accessNoAccessNoLink', { typeTerm }),
      };
    }
    return {
      disabled: false,
      text: '',
    };
  }

  private get participantEmployees(): PersonAsMessageRecipient[] {
    return this.employees.filter((p) => {
      return p.participant;
    });
  }

  private get participantContacts(): PersonAsMessageRecipient[] {
    return this.contacts.filter((p) => {
      return p.participant;
    });
  }

  private get employeesOptions(): SelectCheckboxOption[] {
    const employees = this.showAllEmployees ? this.employees : this.participantEmployees;
    return employees.map((p) => {
      return {
        value: p.uuid,
        text: p.name,
        sublabel: this.userAccessData(p).text,
        disabled: this.userAccessData(p).disabled,
      };
    });
  }

  private get contactsOptions(): SelectCheckboxOption[] {
    const contacts = this.showAllContacts ? this.contacts : this.participantContacts;
    return contacts.map((p) => {
      return {
        value: p.uuid,
        text: p.name,
        sublabel: this.userAccessData(p).text,
        disabled: this.userAccessData(p).disabled,
      };
    });
  }

  toggleShowAllEmployees() {
    this.showAllEmployees = !this.showAllEmployees;
    if (!this.showAllEmployees) {
      this.recipientEmployees = this.recipientEmployees.filter((r) => {
        return this.participants.includes(r);
      });
    }
  }

  toggleShowAllContacts() {
    this.showAllContacts = !this.showAllContacts;
    if (!this.showAllContacts) {
      this.recipientContacts = this.recipientContacts.filter((r) => {
        return this.participants.includes(r);
      });
    }
  }

  onMeetingMessageCommentChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.meetingMessageComment = e.detail.value;
  }

  isPersonSelectable(uuid: string): boolean {
    const person = this.employees.concat(this.contacts).find((p) => {
      return p.uuid === uuid;
    });
    if (person) {
      return !this.userAccessData(person).disabled;
    }
    return false;
  }

  onMessageIncludeContentChanged(e: CustomEvent<{ checked: boolean }>) {
    e.stopPropagation();
    this.messageIncludeContent = e.detail.checked;
    this.recipientEmployees = this.recipientEmployees.filter((uuid) => {
      return this.isPersonSelectable(uuid);
    });
    this.recipientContacts = this.recipientContacts.filter((uuid) => {
      return this.isPersonSelectable(uuid);
    });
  }

  renderEmployees() {
    let labelAction = '';
    if (this.type === 'report' && this.participantEmployees.length !== this.employees.length) {
      labelAction = this.showAllEmployees ? t('meetings.showOnlyParticipants') : t('meetings.showAll');
    }
    return html` <d-select-checkbox
      checklist
      wrap-label
      italic-sublabel
      disabled-x
      class="fullWidth"
      label="${t('meetings.staffLabel')}"
      .options=${this.employeesOptions}
      .labelAction=${labelAction}
      label-action-plain
      .value=${this.recipientEmployees}
      @value-changed=${(e) => (this.recipientEmployees = e.detail.value)}
      @label-action=${() => this.toggleShowAllEmployees()}
    ></d-select-checkbox>`;
  }

  renderContacts() {
    if (this.contacts.length) {
      let labelAction = '';
      if (this.type === 'report') {
        if (this.participantContacts.length === 0) {
          labelAction = this.showAllContacts ? t('meetings.hide') : t('meetings.show');
        } else if (this.participantContacts.length !== this.contacts.length) {
          labelAction = this.showAllContacts ? t('meetings.showOnlyParticipants') : t('meetings.showAll');
        }
      }
      return html` <d-select-checkbox
        checklist
        wrap-label
        italic-sublabel
        disabled-x
        class="fullWidth"
        label="${t('meetings.contactsLabel')}"
        .options=${this.contactsOptions}
        .labelAction=${labelAction}
        label-action-plain
        .value=${this.recipientContacts}
        @value-changed=${(e) => (this.recipientContacts = e.detail.value)}
        @label-action=${() => this.toggleShowAllContacts()}
      ></d-select-checkbox>`;
    }
    return html``;
  }

  renderBody() {
    return html`
      <d-section>
        <d-view-info .content=${this.prompt}></d-view-info>
      </d-section>
      <d-section topless>
        <d-edit-textarea
          class="alertMessage"
          max-rows="50"
          placeholder="${t('meetings.commentPlaceholder')}"
          .value="${this.meetingMessageComment}"
          @value-changed=${this.onMeetingMessageCommentChanged}
        ></d-edit-textarea>
      </d-section>
      ${this.preventIncludeContent
        ? nothing
        : html`
            <d-section topless>
              <d-checkbox
                .checked=${this.messageIncludeContent}
                .optionLabel=${this.includeContentText}
                @checked-changed=${this.onMessageIncludeContentChanged}
              ></d-checkbox>
            </d-section>
          `}
      <d-smooth-resize>
        <d-section> ${this.renderEmployees()} ${this.renderContacts()}</d-section>
      </d-smooth-resize>
    `;
  }

  protected fetchResult(detail: string | undefined): MeetingMessageDialogResult {
    if (detail === 'send') {
      return {
        action: 'send',
        employeeRecipients: this.recipientEmployees,
        contactRecipients: this.recipientContacts,
        message: this.meetingMessageComment,
        includeContent: this.messageIncludeContent,
      };
    }
    return {
      action: 'cancel',
    };
  }

  protected initializeDialog(input: MeetingMessageDialogInput) {
    this.type = input.type;
    this.isEmployeeInterview = input.isEmployeeInterview;
    this.nccEnabled = input.nccEnabled ?? false;
    this.name = input.name ?? '';
    this.participants = input.participants;
    this.people = input.people;
    this.recipientEmployees = this.people
      .filter((p) => {
        return p.type === 'employee' && p.participant && !this.userAccessData(p).disabled;
      })
      .map((p) => p.uuid);
    this.recipientContacts = this.people
      .filter((p) => {
        return p.type === 'contact' && p.participant && !this.userAccessData(p).disabled;
      })
      .map((p) => p.uuid);
    this.classification = input.classification;
    this.accessControl = input.accessControl;
    this.preventIncludeContent = input.preventIncludeContent;
    this.setDefaultValues();
  }

  private setDefaultValues() {
    // Set NCC default message and include agenda for employee interviews
    if (this.nccEnabled && this.isEmployeeInterview && this.type === 'notice') {
      this.meetingMessageComment = t('meetings.nccDefaultMessage');
      this.messageIncludeContent = true;
    } else {
      this.meetingMessageComment = '';
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-meeting-message-popup': MeetingMessageDialog;
  }
}
